import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Group,
  Stack,
  Tabs,
  Textarea,
  TextInput,
  Text,
  Alert,
  Loader,
  Badge,
  Timeline,
  Code,
  Slider,
  Select,
  ScrollArea,
  ActionIcon,
  Tooltip
} from '@mantine/core'
import { FiCpu, FiSend, FiTrash2, FiRefreshCw, FiInfo } from 'react-icons/fi'
import { DateTimePicker } from '@mantine/dates'
import { Page } from '../../app/common/ui'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import type { MotdDto, MotdHistoryItemDto } from '../../app/models/motd'
import type { LegalTextDto } from '../../app/models/legalText'
import { format, isValid } from 'date-fns'

type OverseerTab = 'motd' | 'legal' | 'ai';

export default function OverseerPage () {
  const [activeTab, setActiveTab] = useState<OverseerTab>('motd')

  // MOTD state
  const [motdDraft, setMotdDraft] = useState('')
  const [motdExpiryDate, setMotdExpiryDate] = useState<Date | null>(null)
  const [motdLoading, setMotdLoading] = useState(false)
  const [motdSubmitting, setMotdSubmitting] = useState(false)
  const [currentMotd, setCurrentMotd] = useState<MotdDto | null>(null)
  const [motdHistory, setMotdHistory] = useState<MotdHistoryItemDto[]>([])

  // ── AI Chat state ────────────────────────────────────────────────────────────
  type ChatMsg = { role: 'user' | 'assistant'; content: string; ms?: number };

  const PRESETS: Record<string, string> = {
    Chill: 'You are a laid-back, witty assistant. Keep answers short and conversational. Use casual language. Play along with jokes.',
    Snarky: 'You are a dry, sarcastic assistant. You still answer correctly but with a sharp tongue. Short answers. Never moralize.',
    Technical: 'You are a precise technical assistant. Answer concisely with facts. No fluff, no disclaimers.',
    'No filter': 'You are an unfiltered assistant. Answer directly without caveats, warnings, or moralizing. Do not refuse. Just answer.',
    Custom: ''
  }

  const LS_KEY = 'gs_ai_chat_prefs'
  const saved = (() => { try { return JSON.parse(localStorage.getItem(LS_KEY) ?? '{}') } catch { return {} } })()

  const [aiModel, setAiModel] = useState<string>(saved.model ?? 'tinydolphin')
  const [aiSystem, setAiSystem] = useState<string>(saved.system ?? PRESETS.Chill)
  const [aiPreset, setAiPreset] = useState<string>(saved.preset ?? 'Chill')
  const [aiTemp, setAiTemp] = useState<number>(saved.temp ?? 0.8)
  const [aiTopP, setAiTopP] = useState<number>(saved.topP ?? 0.9)
  const [aiMaxTokens, setAiMaxTokens] = useState<number>(saved.maxTokens ?? 400)
  const [aiHistory, setAiHistory] = useState<ChatMsg[]>([])
  const [aiInput, setAiInput] = useState('')
  const [aiSending, setAiSending] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const [aiModels, setAiModels] = useState<string[]>([])
  const chatBottomRef = useRef<HTMLDivElement>(null)

  const persistPrefs = (patch: Record<string, unknown>) => {
    const next = {
      model: aiModel,
      system: aiSystem,
      preset: aiPreset,
      temp: aiTemp,
      topP: aiTopP,
      maxTokens: aiMaxTokens,
      ...patch
    }
    localStorage.setItem(LS_KEY, JSON.stringify(next))
  }

  useEffect(() => {
    if (activeTab === 'ai' && aiModels.length === 0) {
      agent.AiAssist.ollamaModels()
        .then(r => setAiModels(r.models ?? []))
        .catch(() => {})
    }
  }, [activeTab])

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [aiHistory])

  const handleAiSend = async () => {
    const text = aiInput.trim()
    if (!text || aiSending) return
    setAiInput('')
    setAiError(null)

    const newHistory: ChatMsg[] = [...aiHistory, { role: 'user', content: text }]
    setAiHistory(newHistory)
    setAiSending(true)

    const userMsgs = newHistory.filter(m => m.role === 'user').map(m => m.content)
    const asstMsgs = newHistory.filter(m => m.role === 'assistant').map(m => m.content)
    const t0 = performance.now()
    try {
      const data = await agent.AiAssist.ollamaChat(userMsgs, asstMsgs, {
        model: aiModel,
        system: aiSystem,
        temperature: aiTemp,
        topP: aiTopP,
        maxTokens: aiMaxTokens
      })
      const ms = Math.round(performance.now() - t0)
      setAiHistory(h => [...h, { role: 'assistant', content: data.response, ms }])
    } catch (e: unknown) {
      setAiError(e instanceof Error ? e.message : String(e))
      setAiHistory(h => h.slice(0, -1)) // undo optimistic user msg
      setAiInput(text)
    } finally {
      setAiSending(false)
    }
  }

  // Legal text state
  const [legalTexts, setLegalTexts] = useState<LegalTextDto[]>([])
  const [legalLoading, setLegalLoading] = useState(false)
  const [legalSubmitting, setLegalSubmitting] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editingBody, setEditingBody] = useState('')
  const [newBlockKey, setNewBlockKey] = useState('')
  const [newBlockBody, setNewBlockBody] = useState('')

  // Load MOTD on mount
  useEffect(() => {
    loadMotd()
  }, [])

  // Load legal texts when tab changes
  useEffect(() => {
    if (activeTab === 'legal') {
      loadLegalTexts()
    }
  }, [activeTab])

  const loadMotd = async () => {
    setMotdLoading(true)
    try {
      const result = await agent.Motd.get()
      if (result) {
        setCurrentMotd(result)
      }
      const history = await agent.Motd.history(1, 20)
      setMotdHistory(history?.items || [])
    } catch (err) {
      toast.error('Failed to load MOTD')
    } finally {
      setMotdLoading(false)
    }
  }

  const loadLegalTexts = async () => {
    setLegalLoading(true)
    try {
      const texts = await agent.LegalText.getAll()
      setLegalTexts(texts || [])
    } catch (err) {
      toast.error('Failed to load legal texts')
    } finally {
      setLegalLoading(false)
    }
  }

  const handleMotdPublish = async () => {
    if (!motdDraft.trim()) {
      toast.error('Message cannot be empty')
      return
    }

    setMotdSubmitting(true)
    try {
      const expiryStr = motdExpiryDate ? motdExpiryDate.toISOString() : null
      await agent.Motd.set(motdDraft.trim(), expiryStr)
      toast.success('Message of the Day updated')
      setMotdDraft('')
      setMotdExpiryDate(null)
      await loadMotd()
    } catch (err) {
      toast.error('Failed to update MOTD')
    } finally {
      setMotdSubmitting(false)
    }
  }

  const handleLegalTextSave = async (key: string, body: string) => {
    if (!body.trim()) {
      toast.error('Body cannot be empty')
      return
    }

    setLegalSubmitting(true)
    try {
      await agent.LegalText.upsert(key, body.trim())
      toast.success(`Legal text "${key}" updated`)
      setEditingKey(null)
      setEditingBody('')
      await loadLegalTexts()
    } catch (err) {
      toast.error('Failed to update legal text')
    } finally {
      setLegalSubmitting(false)
    }
  }

  const handleNewBlock = async () => {
    if (!newBlockKey.trim()) {
      toast.error('Key cannot be empty')
      return
    }
    if (!newBlockBody.trim()) {
      toast.error('Body cannot be empty')
      return
    }

    setLegalSubmitting(true)
    try {
      await agent.LegalText.upsert(newBlockKey.trim(), newBlockBody.trim())
      toast.success(`New legal text "${newBlockKey}" created`)
      setNewBlockKey('')
      setNewBlockBody('')
      await loadLegalTexts()
    } catch (err) {
      toast.error('Failed to create legal text')
    } finally {
      setLegalSubmitting(false)
    }
  }

  return (
    <Page title="Overseer — Admin Settings">
      <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
        <Tabs value={activeTab} onTabChange={(v) => setActiveTab((v as OverseerTab) || 'motd')}>
          <Tabs.List>
            <Tabs.Tab value="motd">Message of the Day</Tabs.Tab>
            <Tabs.Tab value="legal">Legal Text</Tabs.Tab>
            <Tabs.Tab value="ai" icon={<FiCpu size={14} />}>AI / Chat</Tabs.Tab>
          </Tabs.List>

          {/* MOTD Tab */}
          <Tabs.Panel value="motd" pt="xl">
            {motdLoading ? (
              <Box sx={{ textAlign: 'center', padding: '40px' }}>
                <Loader />
              </Box>
            ) : (
              <Stack gap="lg">
                {/* Current MOTD */}
                {currentMotd && (
                  <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                    <Stack gap="sm">
                      <Group position="apart">
                        <Text weight={600}>Current MOTD</Text>
                        <Badge color="green">Active</Badge>
                      </Group>
                      <Text sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{currentMotd.message}</Text>
                      <Group spacing="xs" position="apart">
                        <Text size="xs" color="dimmed">
                          Set by @{currentMotd.setByUsername} on{' '}
                          {currentMotd.setAt ? format(new Date(currentMotd.setAt), 'MMM d, yyyy HH:mm') : '—'}
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                )}

                {/* Update Form */}
                <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                  <Stack gap="md">
                    <Text weight={600} size="lg">
                      Publish New MOTD
                    </Text>

                    <Textarea
                      label="Message"
                      placeholder="Enter new message of the day"
                      value={motdDraft}
                      onChange={(e) => setMotdDraft(e.currentTarget.value)}
                      minRows={4}
                      disabled={motdSubmitting}
                    />

                    <DateTimePicker
                      label="Optional Auto-Expiry"
                      description="Leave blank for no scheduled end."
                      value={motdExpiryDate}
                      onChange={setMotdExpiryDate}
                      clearable
                      disabled={motdSubmitting}
                    />

                    <Button
                      onClick={handleMotdPublish}
                      disabled={!motdDraft.trim() || motdSubmitting}
                      loading={motdSubmitting}
                    >
                      Publish MOTD
                    </Button>
                  </Stack>
                </Card>

                {/* History */}
                {motdHistory.length > 0 && (
                  <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                    <Stack gap="md">
                      <Text weight={600}>History</Text>
                      <Timeline active={-1} bulletSize={24} lineWidth={2}>
                        {motdHistory.map((item) => (
                          <Timeline.Item
                            key={item.id}
                            bullet={item.isActive ? '✓' : '—'}
                            title={
                              <Group position="apart" spacing="xs">
                                <Text size="sm" weight={500}>
                                  {item.isActive ? 'Active' : 'Ended'}
                                </Text>
                                <Text size="xs" color="dimmed">
                                  {item.setAt ? format(new Date(item.setAt), 'MMM d HH:mm') : '—'}
                                </Text>
                              </Group>
                            }
                          >
                            <Text size="sm" mt={4} sx={{ whiteSpace: 'pre-wrap' }}>
                              {item.message}
                            </Text>
                            <Text size="xs" color="dimmed" mt={4}>
                              Set by @{item.setByUsername ?? '—'}
                              {item.endedByUsername && ` · Ended by @${item.endedByUsername}`}
                            </Text>
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    </Stack>
                  </Card>
                )}
              </Stack>
            )}
          </Tabs.Panel>

          {/* Legal Text Tab */}
          <Tabs.Panel value="legal" pt="xl">
            {legalLoading ? (
              <Box sx={{ textAlign: 'center', padding: '40px' }}>
                <Loader />
              </Box>
            ) : (
              <Stack gap="lg">
                <Alert icon={<FiInfo />} title="Legal Text Management" color="blue">
                  Manage versioned legal text blocks (consent, terms, policies). Version numbers automatically increment on updates.
                </Alert>

                {/* Existing legal texts */}
                {legalTexts.map((item) => (
                  <Card
                    key={item.key}
                    sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }}
                    p="md"
                  >
                    {editingKey === item.key
                      ? (
                      <Stack gap="md">
                        <Group position="apart">
                          <Group>
                            <Badge color="blue">{item.key}</Badge>
                            <Text size="sm" color="dimmed">
                              v{item.version}
                            </Text>
                          </Group>
                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => setEditingKey(null)}
                            disabled={legalSubmitting}
                          >
                            Cancel
                          </Button>
                        </Group>

                        <Textarea
                          value={editingBody}
                          onChange={(e) => setEditingBody(e.currentTarget.value)}
                          minRows={6}
                          disabled={legalSubmitting}
                        />

                        <Button
                          onClick={() => handleLegalTextSave(item.key, editingBody)}
                          disabled={!editingBody.trim() || legalSubmitting}
                          loading={legalSubmitting}
                        >
                          Save
                        </Button>
                      </Stack>
                        )
                      : (
                      <Stack gap="sm">
                        <Group position="apart">
                          <Group>
                            <Badge color="blue">{item.key}</Badge>
                            <Text size="sm" color="dimmed">
                              v{item.version}
                            </Text>
                          </Group>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => {
                              setEditingKey(item.key)
                              setEditingBody(item.body)
                            }}
                          >
                            Edit
                          </Button>
                        </Group>

                        <Text sx={{ whiteSpace: 'pre-wrap', fontSize: 12, fontFamily: 'monospace' }}>
                          {item.body}
                        </Text>

                        <Group spacing="xs" position="apart">
                          <Text size="xs" color="dimmed">
                            Updated by @{item.updatedByUsername ?? '—'} on{' '}
                            {format(new Date(item.lastUpdatedAt), 'MMM d, yyyy HH:mm')}
                          </Text>
                        </Group>
                      </Stack>
                        )}
                  </Card>
                ))}

                {/* New block form */}
                <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                  <Stack gap="md">
                    <Text weight={600}>+ New Block</Text>

                    <TextInput
                      label="Key"
                      placeholder="e.g., slop-feed-consent"
                      value={newBlockKey}
                      onChange={(e) => setNewBlockKey(e.currentTarget.value)}
                      disabled={legalSubmitting}
                    />

                    <Textarea
                      label="Body"
                      placeholder="Enter the full text..."
                      value={newBlockBody}
                      onChange={(e) => setNewBlockBody(e.currentTarget.value)}
                      minRows={6}
                      disabled={legalSubmitting}
                    />

                    <Button
                      onClick={handleNewBlock}
                      disabled={!newBlockKey.trim() || !newBlockBody.trim() || legalSubmitting}
                      loading={legalSubmitting}
                    >
                      Create Block
                    </Button>
                  </Stack>
                </Card>
              </Stack>
            )}
          </Tabs.Panel>
          {/* AI Chat Tab */}
          <Tabs.Panel value="ai" pt="xl">
            <Stack gap="lg">

              {/* ── Settings card ─────────────────────────────────────────── */}
              <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                <Stack gap="md">
                  <Group position="apart">
                    <Text weight={600}>Model &amp; Prompt Settings</Text>
                    <Tooltip label="Reload model list from Ollama">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => agent.AiAssist.ollamaModels().then(r => setAiModels(r.models ?? [])).catch(() => {})}
                      >
                        <FiRefreshCw size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  {/* Preset buttons */}
                  <Box>
                    <Text size="xs" color="dimmed" mb={6}>System Preset</Text>
                    <Group spacing="xs">
                      {Object.keys(PRESETS).map(name => (
                        <Button
                          key={name}
                          size="xs"
                          variant={aiPreset === name ? 'filled' : 'default'}
                          sx={aiPreset === name ? { background: 'var(--gs-accent)', color: '#fff' } : {}}
                          onClick={() => {
                            setAiPreset(name)
                            if (name !== 'Custom') {
                              setAiSystem(PRESETS[name])
                              persistPrefs({ preset: name, system: PRESETS[name] })
                            } else {
                              persistPrefs({ preset: name })
                            }
                          }}
                        >
                          {name}
                        </Button>
                      ))}
                    </Group>
                  </Box>

                  {/* System prompt */}
                  <Textarea
                    label="System Prompt"
                    value={aiSystem}
                    onChange={(e) => {
                      setAiSystem(e.currentTarget.value)
                      setAiPreset('Custom')
                      persistPrefs({ system: e.currentTarget.value, preset: 'Custom' })
                    }}
                    minRows={3}
                    autosize
                    styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                  />

                  {/* Model selector */}
                  <Select
                    label="Model"
                    value={aiModel}
                    onChange={(v) => { if (v) { setAiModel(v); persistPrefs({ model: v }) } }}
                    data={aiModels.length > 0
                      ? aiModels.map(m => ({ value: m, label: m }))
                      : [{ value: aiModel, label: aiModel + ' (not yet loaded)' }]}
                    styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                  />

                  {/* Sliders */}
                  <Box>
                    <Text size="xs" color="dimmed" mb={4}>Temperature — {aiTemp.toFixed(2)}</Text>
                    <Slider
                      min={0} max={2} step={0.05}
                      value={aiTemp}
                      onChange={(v) => { setAiTemp(v); persistPrefs({ temp: v }) }}
                      color="orange"
                      size="xs"
                      marks={[{ value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }]}
                    />
                  </Box>

                  <Box mt={8}>
                    <Text size="xs" color="dimmed" mb={4}>Top-P — {aiTopP.toFixed(2)}</Text>
                    <Slider
                      min={0} max={1} step={0.05}
                      value={aiTopP}
                      onChange={(v) => { setAiTopP(v); persistPrefs({ topP: v }) }}
                      color="orange"
                      size="xs"
                      marks={[{ value: 0, label: '0' }, { value: 0.5, label: '0.5' }, { value: 1, label: '1' }]}
                    />
                  </Box>

                  <Box mt={8}>
                    <Text size="xs" color="dimmed" mb={4}>Max Tokens — {aiMaxTokens}</Text>
                    <Slider
                      min={100} max={1000} step={50}
                      value={aiMaxTokens}
                      onChange={(v) => { setAiMaxTokens(v); persistPrefs({ maxTokens: v }) }}
                      color="orange"
                      size="xs"
                      marks={[{ value: 100, label: '100' }, { value: 500, label: '500' }, { value: 1000, label: '1000' }]}
                    />
                  </Box>
                </Stack>
              </Card>

              {/* ── Chat card ─────────────────────────────────────────────── */}
              <Card sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)' }} p="md">
                <Stack gap="md">
                  <Group position="apart">
                    <Text weight={600}>Chat</Text>
                    <Tooltip label="Clear conversation">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => { setAiHistory([]); setAiError(null) }}
                        disabled={aiHistory.length === 0}
                      >
                        <FiTrash2 size={13} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  {/* Message history */}
                  <ScrollArea h={360} sx={{ border: '1px solid var(--gs-border)', borderRadius: 6 }} p="sm">
                    {aiHistory.length === 0 && (
                      <Text size="xs" color="dimmed" sx={{ textAlign: 'center', paddingTop: 120 }}>
                        No messages yet. Say something.
                      </Text>
                    )}
                    <Stack gap="xs">
                      {aiHistory.map((msg, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '80%',
                              background: msg.role === 'user'
                                ? 'var(--gs-accent)'
                                : 'var(--gs-surface3, #2a2a2a)',
                              color: msg.role === 'user' ? '#fff' : 'inherit',
                              borderRadius: 8,
                              padding: '8px 12px',
                              fontSize: 13,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {msg.content}
                            {msg.ms !== undefined && (
                              <Text size={10} sx={{ opacity: 0.6, marginTop: 4 }}>{msg.ms} ms</Text>
                            )}
                          </Box>
                        </Box>
                      ))}
                      {aiSending && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          <Box sx={{ background: 'var(--gs-surface3, #2a2a2a)', borderRadius: 8, padding: '8px 12px' }}>
                            <Group spacing={6}>
                              <Loader size={12} color="orange" />
                              <Text size="xs" color="dimmed">Thinking…</Text>
                            </Group>
                          </Box>
                        </Box>
                      )}
                    </Stack>
                    <div ref={chatBottomRef} />
                  </ScrollArea>

                  {aiError && (
                    <Alert color="red" title="Error" withCloseButton onClose={() => setAiError(null)}>
                      {aiError}
                    </Alert>
                  )}

                  {/* Input row */}
                  <Group align="flex-end" spacing="xs">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Textarea
                        placeholder="Type a message… (Shift+Enter for newline)"
                        value={aiInput}
                        onChange={(e) => setAiInput(e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleAiSend()
                          }
                        }}
                        minRows={2}
                        autosize
                        maxRows={6}
                        disabled={aiSending}
                      />
                    </Box>
                    <Tooltip label="Send (Enter)">
                      <ActionIcon
                        size="xl"
                        onClick={handleAiSend}
                        loading={aiSending}
                        disabled={!aiInput.trim() || aiSending}
                        sx={{
                          background: 'var(--gs-accent)',
                          color: '#fff',
                          '&:hover': { background: 'var(--gs-accent-hover)' },
                          '&[data-disabled]': { opacity: 0.4 }
                        }}
                      >
                        <FiSend size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Stack>
              </Card>

            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Page>
  )
}
